import identifyQualifiedTraffic from "@utils/identifyQualifiedTraffic";
import { handleCustomUDL } from "@utils/UDLUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  active: "active",
};

const SELECTORS = {
  tab: ".js-tab",
  tabContent: ".js-tab-content",
  level1NonTabLink: ".navlevelone a:not(.js-tab)",
  level2Link: ".navleveltwo > a",
  level3Link: ".navlevelthree > a",
  activeTab: `.js-tab.${CLASSES.active}`,
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class NavFlyout {
  private el: HTMLElement;
  private abortController: AbortController;

  // Cached elements
  private tabs: NodeListOf<HTMLAnchorElement>;
  private tabContents: NodeListOf<HTMLElement>;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.tabs = el.querySelectorAll<HTMLAnchorElement>(SELECTORS.tab);
    this.tabContents = el.querySelectorAll<HTMLElement>(SELECTORS.tabContent);
  }

  init(): void {
    this.bindEvents();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => this.clickTab(e, tab), { signal });
      tab.addEventListener("dblclick", () => tab.click(), { signal });
    });

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level1NonTabLink)
      .forEach((link) => {
        link.addEventListener("click", () => this.clickLevel1Link(link), { signal });
      });

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level2Link)
      .forEach((link) => {
        link.addEventListener("click", () => this.clickLevel2Link(link), { signal });
      });

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level3Link)
      .forEach((link) => {
        link.addEventListener("click", () => this.clickLevel3Link(link), { signal });
      });
  }

  // ─── Tab click handler ──────────────────────────────────────────────────────

  private clickTab(event: MouseEvent, tab: HTMLAnchorElement): void {
    if (!tab.classList.contains(CLASSES.active)) {
      this.tabContents.forEach((tc) => tc.classList.remove(CLASSES.active));
      this.tabs.forEach((t) => t.classList.remove(CLASSES.active));

      tab.classList.add(CLASSES.active);

      const target = tab.dataset["target"];
      if (target) {
        this.el.querySelector(`#${target}`)?.classList.add(CLASSES.active);
      }

      this.el.dispatchEvent(new CustomEvent("navigationChanged", { bubbles: true }));

      event.preventDefault();
    } else {
      this.pushData({
        link_url: tab.href,
        navigation_level_1: tab.innerText,
        navigation_level_2: undefined,
        navigation_level_3: undefined,
      });
    }
  }

  // ─── Level-1 link handler ───────────────────────────────────────────────────

  private clickLevel1Link(link: HTMLAnchorElement): void {
    const link_url = link.href;

    this.pushData({
      link_url,
      navigation_level_1: link.innerText,
      navigation_level_2: undefined,
      navigation_level_3: undefined,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── Level-2 link handler ───────────────────────────────────────────────────

  private clickLevel2Link(link: HTMLAnchorElement): void {
    const activeTab = this.el.querySelector<HTMLAnchorElement>(SELECTORS.activeTab);
    const link_url = link.href;

    this.pushData({
      link_url,
      navigation_level_1: activeTab?.innerText,
      navigation_level_2: link.innerText,
      navigation_level_3: undefined,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── Level-3 link handler ───────────────────────────────────────────────────

  private clickLevel3Link(link: HTMLAnchorElement): void {
    const activeTab = this.el.querySelector<HTMLAnchorElement>(SELECTORS.activeTab);
    const link_url = link.href;

    this.pushData({
      link_url,
      navigation_level_1: activeTab?.innerText,
      navigation_level_2: link
        .closest(".navleveltwo")
        ?.querySelector<HTMLAnchorElement>(":scope > a")?.innerText,
      navigation_level_3: link.innerText,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── UDL push helper ────────────────────────────────────────────────────────

  private pushData(params: Record<string, unknown>): void {
    handleCustomUDL("top_navigation", params);
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="nav-flyout"]')
      .forEach((el) => {
        new NavFlyout(el).init();
      });
  }
}
